import assign from "lodash.assign";
import paymentService from "../services/paymentService";
import billingAddressForm from "./billingAddressForm";
import { setErrorInErrors, clearErrorFromErrors } from "../utils/errorsUtil";
import { PAYMENT_METHODS, STRIPE_CARD_FUNDING_TYPES, LOCATION_CHANGE_ACTIONS } from "common/constants/Constants";
import ERROR_MESSAGES from "common/constants/ErrorMessages";
import GoogleTracking from "../services/GoogleTracking";
import isPaymentValidated from "utils/isPaymentValidated";
import { LOCATION_CHANGE } from "react-router-redux";

export const PAYMENT_FORM_SET_STRIPE_PROVIDER = "PAYMENT_FORM_SET_STRIPE_PROVIDER";
export const PAYMENT_FORM_UPDATE_CARD = "PAYMENT_FORM_UPDATE_CARD";
export const PAYMENT_FORM_SHOW_ERROR = "PAYMENT_FORM_SHOW_ERROR";
export const PAYMENT_FORM_CLEAR_ERROR = "PAYMENT_FORM_CLEAR_ERROR";
export const PAYMENT_FORM_UPDATE_PAYMENT_METHOD = "PAYMENT_FORM_UPDATE_PAYMENT_METHOD";
export const PAYMENT_FORM_UPDATE_PAYPAL_ID = "PAYMENT_FORM_UPDATE_PAYPAL_ID";
export const PAYMENT_FORM_UPDATE_PAYPAL_EMAIL = "PAYMENT_FORM_UPDATE_PAYPAL_EMAIL";
export const PAYMENT_FORM_SET_STRIPE_PAYMENT_PROVIDER = "PAYMENT_FORM_SET_STRIPE_PAYMENT_PROVIDER";
export const PAYMENT_FORM_SHOW_LOADING = "PAYMENT_FORM_SHOW_LOADING";

const initialState = { errors: {}, cardInformation: {}, showLoading: false };

export function paymentFormReducer(state = initialState, action) {
    switch (action.type) {
        case PAYMENT_FORM_SET_STRIPE_PROVIDER:
            return assign({}, state, {
                stripe: action.stripe
            });
        case PAYMENT_FORM_SET_STRIPE_PAYMENT_PROVIDER:
            return assign({}, state, {
                stripePaymentProvider: action.stripePaymentProvider
            });
        case PAYMENT_FORM_UPDATE_CARD:
            return assign({}, state, {
                cardInformation: action.cardInformation
            });
        case PAYMENT_FORM_SHOW_ERROR:
            return assign({}, state, {
                errors: setErrorInErrors(state.errors, action.error)
            });
        case PAYMENT_FORM_CLEAR_ERROR:
            return assign({}, state, {
                errors: clearErrorFromErrors(state.errors, action.errorType)
            });
        case PAYMENT_FORM_UPDATE_PAYMENT_METHOD:
            return assign({}, state, {
                paymentMethod: action.paymentMethod
            });
        case PAYMENT_FORM_UPDATE_PAYPAL_ID:
            return assign({}, state, {
                paypalId: action.paypalId
            });
        case PAYMENT_FORM_UPDATE_PAYPAL_EMAIL:
            return assign({}, state, {
                paypalEmail: action.paypalEmail
            });
        case PAYMENT_FORM_SHOW_LOADING:
            return assign({}, state, {
                showLoading: action.showLoading
            });
        case LOCATION_CHANGE:
            // only reset the state if the user hit the brower's back button
            return action.payload.action === LOCATION_CHANGE_ACTIONS.POP ? { ...initialState } : state;
        default:
            return state;
    }
}

export function setStripeProvider(stripe) {
    return {
        type: PAYMENT_FORM_SET_STRIPE_PROVIDER,
        stripe
    };
}

export function setStripePaymentProvider(stripePaymentProvider) {
    return {
        type: PAYMENT_FORM_SET_STRIPE_PAYMENT_PROVIDER,
        stripePaymentProvider
    };
}

export function updateCardInformation(cardInformation) {
    return {
        type: PAYMENT_FORM_UPDATE_CARD,
        cardInformation
    };
}

export function clearCardInformation() {
    return {
        type: PAYMENT_FORM_UPDATE_CARD,
        cardInformation: {}
    };
}

export function clearPayPalInformation() {
    return {
        type: PAYMENT_FORM_UPDATE_PAYPAL_ID,
        paypalId: null
    };
}

export function updatePaymentMethod(paymentMethod) {
    return {
        type: PAYMENT_FORM_UPDATE_PAYMENT_METHOD,
        paymentMethod
    };
}

export function updatePayPalId(paypalId) {
    return {
        type: PAYMENT_FORM_UPDATE_PAYPAL_ID,
        paypalId
    };
}

export function updatePayPalEmail(paypalEmail) {
    return {
        type: PAYMENT_FORM_UPDATE_PAYPAL_EMAIL,
        paypalEmail
    };
}

export function showError(error) {
    return {
        type: PAYMENT_FORM_SHOW_ERROR,
        error
    };
}

export function clearError(errorType) {
    return {
        type: PAYMENT_FORM_CLEAR_ERROR,
        errorType
    };
}

export function showLoading() {
    return {
        type: PAYMENT_FORM_SHOW_LOADING,
        showLoading: true
    };
}

export function clearLoading() {
    return {
        type: PAYMENT_FORM_SHOW_LOADING,
        showLoading: false
    };
}

export function conditionallyShowLoading() {
    return (dispatch, getState) => {
        const {
            paymentForm: {
                cardInformation,
                paypalId
            }
        } = getState();

        const paymentValidated = isPaymentValidated(cardInformation, paypalId);

        if (!paymentValidated) {
            dispatch(showLoading());
        }
    };
}

export function submitCard(grecaptchaToken, skipGrecaptcha) {
    return (dispatch, getState) => {
        const {
            paymentForm: {
                cardInformation,
                stripe
            },
            loginRegister: {
                email
            },
            billingAddressForm: {
                postalCode
            }
        } = getState();

        if (cardInformation.id) {
            return Promise.resolve({});
        }

        return paymentService.getStripeToken(stripe)
            .then((token) => paymentService.addCardToStripe(token, email, grecaptchaToken, "creditcard", { postalCode }, skipGrecaptcha))
            .then((data) => {
                const card = formatCard(data);

                return dispatch(validateCardType(card))
                    .then(() => dispatch(updateCardInformation(card)));
            })
            .then(() => trackPaymentSubmit("creditcard"))
            .then(() => dispatch(clearError())) // clear all errors here to make sure browser pay errors get erased if there were any
            .catch((error) => {
                trackPaymentError(error);
                dispatch(showError({
                    type: "creditCard",
                    message: cardErrorMessage(error)
                }));

                throw error;
            });
    };
}

export function submitPaymentRequest(token) {
    return (dispatch, getState) => {
        const {
            paymentForm: {
                cardInformation,
                stripePaymentProvider
            },
            loginRegister: {
                email
            }
        } = getState();

        if (cardInformation.id) {
            return Promise.resolve({});
        }

        const trackingNames = {
            [PAYMENT_METHODS.APPLE]: "applePay",
            [PAYMENT_METHODS.GOOGLE]: "googlePay"
        };
        const trackingName = trackingNames[stripePaymentProvider] || "unknown";

        return paymentService.addCardToStripe(token, email, null, trackingName)
            .then((data) => {
                const card = formatCard(data);

                /* eslint-disable camelcase */
                const {
                    address_line1,
                    address_line2,
                    address_city,
                    address_country,
                    address_state,
                    address_zip
                } = data;

                if (address_line1) {
                    dispatch(billingAddressForm.validateAndUpdateAddressLine1(address_line1));
                }

                if (address_line2) {
                    dispatch(billingAddressForm.validateAndUpdateAddressLine2(address_line2));
                }

                if (address_city) {
                    dispatch(billingAddressForm.validateAndUpdateCity(address_city));
                }

                if (address_country) {
                    dispatch(billingAddressForm.validateAndUpdateCountry(address_country));
                }

                if (address_state) {
                    dispatch(billingAddressForm.validateAndUpdateState(address_state));
                }

                if (address_zip) {
                    dispatch(billingAddressForm.validateAndUpdatePostalCode(address_zip));
                }
                /* eslint-enable camelcase */

                return dispatch(validateCardType(card))
                    .then(() => dispatch(updateCardInformation(card)));
            })
            .then(() => trackPaymentSubmit(trackingName))
            .then(() => dispatch(clearError(trackingName)))
            .catch((error) => {
                // set the payment method back to stripe if there is an error with browser pay
                dispatch(updatePaymentMethod(PAYMENT_METHODS.STRIPE));
                trackPaymentError(error);
                dispatch(showError({
                    type: trackingName,
                    message: error.errorMessage
                }));

                throw error;
            });
    };
}

export function submitPayPal(paypalInfo) {
    return (dispatch) => {
        const {
            paypalId,
            paypalEmail
        } = paypalInfo;

        dispatch(clearCardInformation());
        dispatch(updatePaymentMethod(PAYMENT_METHODS.PAYPAL));
        dispatch(updatePayPalId(paypalId));
        dispatch(updatePayPalEmail(paypalEmail));
        trackPaymentSubmit("paypal");
    };
}

export function validatePayment(grecaptchaToken, skipGrecaptcha) {
    return (dispatch, getState) => {
        const {
            paymentForm: {
                paymentMethod,
                paypalId,
                cardInformation
            }
        } = getState();

        dispatch(clearError("submitPayment"));

        if ((paymentMethod === PAYMENT_METHODS.APPLE || paymentMethod === PAYMENT_METHODS.GOOGLE) && cardInformation.id) {
            return Promise.resolve({});
        }

        if (paymentMethod === PAYMENT_METHODS.PAYPAL && paypalId) {
            return Promise.resolve({});
        }

        if (paymentMethod === PAYMENT_METHODS.STRIPE) {
            return dispatch(submitCard(grecaptchaToken, skipGrecaptcha));
        }

        // [LW] if payment method isn't defined, or the user doens't have the correct info set, show a generic error
        dispatch(showError({
            type: "submitPayment",
            message: ERROR_MESSAGES.FIELDS_IN_ERROR
        }));

        return Promise.reject();
    };
}

function cardErrorMessage(error) {
    if (error.type === "validation_error") {
        return ERROR_MESSAGES.FIELDS_IN_ERROR;
    }

    return error.errorMessage || error.message;
}

function trackPaymentError(error) {
    GoogleTracking.registerSubscriptionEvent("error", `error:[${ error.message }]`);
}

function trackPaymentSubmit(apiTrackingName) {
    GoogleTracking.registerSubscriptionEvent("submit", `payment:confirm:${ apiTrackingName }`);

    GoogleTracking.registerEvent({
        event: "Keywee Event",
        keyweeEventName: "AddPaymentInfo"
    });
}

function formatCard(stripeCard) {
    return {
        id: stripeCard.id,
        expirationMonth: stripeCard.exp_month,
        expirationYear: stripeCard.exp_year,
        lastFourDigits: stripeCard.last4,
        country: stripeCard.country,
        brand: stripeCard.brand,
        customer: stripeCard.customer,
        funding: stripeCard.funding
    };
}

function validateCardType({ funding }) {
    return (dispatch) => {
        if (funding === STRIPE_CARD_FUNDING_TYPES.PREPAID
                || funding === STRIPE_CARD_FUNDING_TYPES.UNKNOWN) {
            const errorObject = {
                type: "unaccepted_payment_method",
                message: ERROR_MESSAGES.UNSUPPORTED_CARD_TYPE
            };

            dispatch(showError(errorObject));
            trackPaymentError(errorObject);

            return Promise.reject();
        }

        return Promise.resolve({});
    };
}

export default {
    updateCardInformation,
    updatePaymentMethod,
    updatePayPalId,
    updatePayPalEmail,
    showError,
    clearError,
    clearCardInformation,
    clearPayPalInformation,
    setStripeProvider,
    setStripePaymentProvider,
    submitCard,
    submitPaymentRequest,
    submitPayPal,
    validatePayment,
    showLoading,
    clearLoading,
    conditionallyShowLoading,
    validateCardType
};
